import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {Autocomplete, Divider, Stack} from "@mui/material";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import Button from "@mui/material/Button";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import FolioDigitalSigningRow from "./FolioDigitalSigningRow";

interface Props {
    folio: Folio
    setFolio: (folio: Folio) => void
    canEditFolio: boolean
    reservation?: Reservation
}

const FolioBasicInfo = ({folio, setFolio, canEditFolio, reservation}: Props) => {

    const {t} = useTranslation()

    // A POS folio carries the table it was rung on. The id alone tells a member of staff nothing,
    // and it is the one field on this screen that names a physical place in the venue.
    const resourceQuery = useQuery(
        ['folioResource', folio.resourceId],
        () => ResourceService.getResourceById(folio.resourceId!),
        {enabled: !!folio.resourceId}
    )

    const updateField = (field: keyof Folio, value: any) => {
        setFolio({...folio, [field]: value})
    }

    return (
        <Stack gap={6}>
            <Stack gap={2}>
                <Typography variant={'h5'} textAlign={'start'}>
                    {t('Basic info')}
                </Typography>

                <Stack gap={1}>

            <Property
                title={t('Name')}
                tooltip={t('Internal display name for this folio.')}
            >
                <TextField
                    id={'name'}
                    size={'small'}
                    value={folio?.name}
                    onChange={(e) => {
                        updateField('name', e.target.value)
                    }}
                    fullWidth
                    disabled={!canEditFolio}
                />
            </Property>


            <Property
                title={t('Status')}
                tooltip={t('Current folio lifecycle status, usually controlled by billing actions.')}
            >
                <Autocomplete
                    id={'status'}
                    options={[
                        undefined,
                        ...Object.keys(FolioStatus)
                    ]}
                    getOptionLabel={(option: any) => option ? t(`Folio status ${option}`) : t('(not set)')}
                    value={folio.status?.toString() || ''}
                    fullWidth
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    disabled
                />
            </Property>

            <ProfileInfoRow
                id={'profile'}
                profileId={folio.profileId}
                setProfileId={(id) => updateField('profileId', id)}
                onDelete={() => updateField('profileId', undefined)}
                disabled={!canEditFolio}
            />

            {
                reservation?.reservationRequestData &&
                <Property
                    title={t('Reservation request data')}
                    tooltip={t('Original contact details submitted with the reservation request.')}
                >
                    <Stack
                        sx={{
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            p: 1
                        }}
                    >
                        <Typography>
                            {t('First name')}: {reservation.reservationRequestData?.firstName}
                        </Typography>
                        <Typography>
                            {t('Last name')}: {reservation.reservationRequestData?.lastName}
                        </Typography>
                        <Typography>
                            {t('Email')}: {reservation.reservationRequestData?.email}
                        </Typography>
                        <Typography>
                            {t('Phone')}: {reservation.reservationRequestData?.phone}
                        </Typography>

                    </Stack>
                </Property>
            }

            {
                folio.resourceId &&
                <Property
                    title={t('Table')}
                    tooltip={t('The table this charge arose on, taken from the point-of-sale check and matched through the table bindings on the connector.')}
                >
                    <TextField
                        id={'resource'}
                        size={'small'}
                        value={resourceQuery.data?.name || folio.resourceId}
                        fullWidth
                        disabled
                    />
                </Property>
            }

            <Property
                title={t('Reservation')}
                tooltip={t('Reservation this folio belongs to.')}
            >
                <>
                    <TextField
                        id={'reservation'}
                        size={'small'}
                        value={folio.reservationId}
                        fullWidth
                        disabled={!canEditFolio}
                    />

                    {
                        folio.reservationId &&
                        <Button
                            id={'btn-view-reservation'}
                            endIcon={<OpenInNewIcon/>}
                            sx={{minWidth: 'fit-content'}}
                            onClick={() => {
                                window.open(`/system/reservations/${folio.reservationId}`, '_blank')
                            }}
                        >
                            {t('View')}
                        </Button>
                    }

                </>

            </Property>

            <FolioDigitalSigningRow folio={folio}/>

                </Stack>
            </Stack>

            <Divider/>

            <Stack gap={2}>
                <Typography variant={'h5'} textAlign={'start'}>
                    {t('Billing Info')}
                </Typography>

                <Stack gap={1}>

            <Property
                title={t('First Name')}
                tooltip={t('First name of the billing recipient.')}
            >
                <TextField
                    id={'billTo-firstName'}
                    size={'small'}
                    value={folio.billTo?.firstName || ''}
                    onChange={(e) => {
                        const billTo = {...folio.billTo, firstName: e.target.value};
                        updateField('billTo', billTo);
                    }}
                    fullWidth
                    disabled={!canEditFolio}
                />
            </Property>
            <Property
                title={t('Last Name')}
                tooltip={t('Last name of the billing recipient.')}
            >
                <TextField
                    id={'billTo-lastName'}
                    size={'small'}
                    value={folio.billTo?.lastName || ''}
                    onChange={(e) => {
                        const billTo = {...folio.billTo, lastName: e.target.value};
                        updateField('billTo', billTo);
                    }}
                    fullWidth
                    disabled={!canEditFolio}
                />
            </Property>
            <Property
                title={t('Email')}
                tooltip={t('Email address used for billing and invoices.')}
            >
                <TextField
                    id={'billTo-email'}
                    size={'small'}
                    value={folio.billTo?.email || ''}
                    onChange={(e) => {
                        const billTo = {...folio.billTo, email: e.target.value};
                        updateField('billTo', billTo);
                    }}
                    fullWidth
                    disabled={!canEditFolio}
                />
            </Property>
            <Property
                title={t('Phone')}
                tooltip={t('Contact phone number for the billing recipient.')}
            >
                <TextField
                    id={'billTo-phone'}
                    size={'small'}
                    value={folio.billTo?.phone || ''}
                    onChange={(e) => {
                        const billTo = {...folio.billTo, phone: e.target.value};
                        updateField('billTo', billTo);
                    }}
                    fullWidth
                    disabled={!canEditFolio}
                />
            </Property>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default FolioBasicInfo;
